import type ApplicationInstance from '@ember/application/instance';
import { registerDestructor } from '@ember/destroyable';
import { installAuthContextProviders, resetAuthContext } from 'tio-data/auth-context';
import { installAppContextProviders, resetAppContext } from 'tio-data/app-context';
import { onRequestEvent, type TioRequestEvent } from 'tio-data/request-events';
import type TelemetryService from 'tio-common/services/telemetry';
import type SessionService from 'tio-employee/services/session';
import type SessionContextService from 'tio-employee/services/session-context';
import type ImpersonationService from 'tio-employee/services/impersonation';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type NotificationsService from 'tio-ui/services/notifications';
import { resolveDemoScenario } from 'tio-employee/demo/registry';

type RenewableSession = SessionService & {
  expireAt?: number;
  renew?: (token?: string) => Promise<unknown> | unknown;
};

/**
 * ADR-001: Installs live auth, impersonation and demo providers before routes run, and handles request failures.
 * Providers read current session state; destruction removes subscriptions and resets the shared context.
 */
export function initialize(appInstance: ApplicationInstance): void {
  const session = () => appInstance.lookup('service:session') as RenewableSession | undefined;
  const sessionContext = () =>
    appInstance.lookup('service:session-context') as SessionContextService | undefined;
  const telemetry = () => appInstance.lookup('service:telemetry') as TelemetryService | undefined;
  const impersonation = () =>
    appInstance.lookup('service:impersonation') as ImpersonationService | undefined;
  const demoMode = () => appInstance.lookup('service:demo-mode') as DemoModeService | undefined;

  installAuthContextProviders({
    token: () => session()?.data?.authenticated?.access_token,
    isAuthenticated: () => session()?.isAuthenticated ?? false,
    publicKey: () => session()?.publicKey,
    sessionExpiresAt: () => session()?.expireAt,
    renew: (token?: string) => session()?.renew?.(token),
    userId: () => sessionContext()?.user?.id as string | undefined,
  });

  installAppContextProviders({
    impersonation: {
      isImpersonated: () => impersonation()?.isImpersonated ?? false,
      confirmMutation: () => {
        const svc = impersonation();
        return svc ? svc.confirmMutation() : Promise.resolve(true);
      },
    },
    demo: {
      isActive: () => demoMode()?.isActive ?? false,
      resolve: () => {
        const svc = demoMode();
        return svc?.isActive ? resolveDemoScenario(svc.currentSelection) : null;
      },
    },
  });

  const unsubscribe = onRequestEvent((event: TioRequestEvent) => {
    telemetry()?.trackRequest({
      status: event.status,
      method: event.method,
      url: event.url,
      duration: event.duration,
    });
    if (event.type === 'unauthorized') {
      void sessionContext()?.logout();
    } else if (event.type === 'request-failed') {
      try {
        (appInstance.lookup('service:notifications') as NotificationsService | undefined)?.add(
          event.message,
          { appearance: 'error' }
        );
      } catch (toastErr) {
        console.error('Failed to show error notification:', toastErr, 'Original:', event.message);
      }
    }
  });

  registerDestructor(appInstance, () => {
    unsubscribe();
    resetAuthContext();
    resetAppContext();
  });
}

export default { initialize };
